import { useAuth } from "@clerk/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
	Edit,
	Gamepad2,
	Image as ImageIcon,
	Layers,
	Link2,
	Package,
} from "lucide-react"
import type * as React from "react"
import {
	ENTITY_PAGE_CLASS,
	EntityDetailSkeleton,
	EntityDetailSplit,
	StagingMetadataPanel,
	SUMMARY_LINK_CLASS,
	SummaryGroup,
	SummaryLinkContent,
	SummaryPanel,
} from "@/components/catalogue-entity/entity-detail-layout"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
} from "@/components/layout"
import { AspectRatio } from "@/components/ui/aspect-ratio"
import { Badge, type BadgeProps } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useOrpc } from "@/integrations/orpc"
import { useStagingGame } from "@/queries/games/use-staging-game"
import { useProductionEntity } from "@/queries/production/use-production-entity"
import { formatGameName } from "@/utils/game.helpers"

const TABS = ["details", "metadata"] as const

type GameTab = (typeof TABS)[number]

export const Route = createFileRoute("/games/$id/")({
	component: GameDetailPage,
	validateSearch: createTabSearch(TABS),
})

function GameDetailPage() {
	const { id } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { game, isLoading, error } = useStagingGame({
		gameSlug: id,
	})

	const activeTab = searchParams.tab ?? "details"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/games/$id",
			params: { id },
			search: tab === "details" ? {} : { tab: tab as GameTab },
			replace: true,
		})
	}

	const { client } = useOrpc()
	const { isSignedIn } = useAuth()

	const { data: expansionCount } = useQuery({
		queryKey: ["expansionCount", id],
		queryFn: () =>
			client.staging.getEntities({
				entityType: "expansion",
				gameSlug: id,
				limit: 1,
			}),
		select: (data) => data.total,
		enabled: isSignedIn && !!id,
	})

	const { data: productCount } = useQuery({
		queryKey: ["productCount", id],
		queryFn: () =>
			client.staging.getEntities({
				entityType: "product",
				filters: { gameSlug: id },
				limit: 1,
			}),
		select: (data) => data.total,
		enabled: isSignedIn && !!id,
	})

	const { data: productionData } = useProductionEntity({
		gameSlug: id,
		entitySlug: id,
		entityType: "game",
		enabled: isSignedIn && !!id,
	})

	if (isLoading) {
		return <EntityDetailSkeleton />
	}

	if (error || !game) {
		return (
			<div className="p-4 sm:p-6">
				<PageHeader
					title="Game not found"
					description={
						error?.message || `The game with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	const gameData = game.data as Record<string, unknown> & {
		name: string
		slug: string
		logo?: string
		themedLogo?: Record<string, unknown>
		themedIcon?: Record<string, unknown>
		releaseDate?: string
		description?: string
		abbreviation?: string
		website?: string
	}
	const gameSlug = game.gameSlug

	const prodGame = productionData as
		| {
				themedLogo?: Record<string, string>
				themedIcon?: Record<string, string>
		  }
		| undefined

	const logoOriginalUrl = prodGame?.themedLogo?.original ?? null
	const logoDarkUrl = prodGame?.themedLogo?.dark ?? null
	const logoLightUrl = prodGame?.themedLogo?.light ?? null
	const iconDarkUrl = prodGame?.themedIcon?.dark ?? null
	const iconLightUrl = prodGame?.themedIcon?.light ?? null

	const logoUrl = logoOriginalUrl || logoDarkUrl || logoLightUrl || null
	const hasThemedAssets = Boolean(
		logoDarkUrl || logoLightUrl || iconDarkUrl || iconLightUrl,
	)

	return (
		<div className={ENTITY_PAGE_CLASS}>
			<PageHeader
				title={formatGameName(gameSlug)}
				description={
					<span className="flex flex-wrap items-center gap-x-2">
						<span className="font-mono text-foreground">{game.entitySlug}</span>
						{gameData.abbreviation && (
							<>
								<span className="text-meta">·</span>
								<span>{gameData.abbreviation}</span>
							</>
						)}
						<span className="text-meta">·</span>
						<span>Game configuration and expansions</span>
					</span>
				}
				actions={
					<Button asChild>
						<Link to="/games/$id/manage" params={{ id }}>
							<Edit />
							Edit
						</Link>
					</Button>
				}
			/>

			<EntityDetailSplit
				aside={
					<>
						<SummaryPanel title="Assets" icon={ImageIcon}>
							<SummaryGroup label="Logo">
								<AspectRatio
									ratio={16 / 9}
									className="overflow-hidden rounded-md bg-muted"
								>
									{logoUrl ? (
										<img
											src={logoUrl}
											alt={gameData.name}
											className="h-full w-full object-contain"
										/>
									) : (
										<div className="flex h-full items-center justify-center text-meta">
											No logo available
										</div>
									)}
								</AspectRatio>
							</SummaryGroup>

							{hasThemedAssets && (
								<SummaryGroup label="Themed assets">
									<div className="grid grid-cols-2 gap-3">
										{logoDarkUrl && (
											<ThemedAsset label="Logo (dark)">
												<img
													src={logoDarkUrl}
													alt="Logo dark"
													className="h-10 w-auto max-w-full rounded bg-zinc-900 p-1"
												/>
											</ThemedAsset>
										)}
										{logoLightUrl && (
											<ThemedAsset label="Logo (light)">
												<img
													src={logoLightUrl}
													alt="Logo light"
													className="h-10 w-auto max-w-full rounded bg-zinc-100 p-1"
												/>
											</ThemedAsset>
										)}
										{iconDarkUrl && (
											<ThemedAsset label="Icon (dark)">
												<img
													src={iconDarkUrl}
													alt="Icon dark"
													className="h-8 w-8 rounded bg-zinc-900 p-1"
												/>
											</ThemedAsset>
										)}
										{iconLightUrl && (
											<ThemedAsset label="Icon (light)">
												<img
													src={iconLightUrl}
													alt="Icon light"
													className="h-8 w-8 rounded bg-zinc-100 p-1"
												/>
											</ThemedAsset>
										)}
									</div>
								</SummaryGroup>
							)}
						</SummaryPanel>

						<SummaryPanel title="Related content" icon={Link2}>
							<SummaryGroup>
								<Button asChild variant="ghost" className={SUMMARY_LINK_CLASS}>
									<Link to="/catalogue/games/$gameSlug" params={{ gameSlug }}>
										<SummaryLinkContent
											icon={<Layers />}
											label="View expansions"
											value={expansionCount?.toLocaleString()}
										/>
									</Link>
								</Button>
								<Button asChild variant="ghost" className={SUMMARY_LINK_CLASS}>
									<Link to="/products" search={{ gameSlug: id }}>
										<SummaryLinkContent
											icon={<Package />}
											label="View products"
											value={productCount?.toLocaleString()}
										/>
									</Link>
								</Button>
							</SummaryGroup>
						</SummaryPanel>
					</>
				}
			>
				<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-6">
					<TabsList variant="line">
						<TabsTrigger value="details">Details</TabsTrigger>
						<TabsTrigger value="metadata">Metadata</TabsTrigger>
					</TabsList>

					<TabsContent value="details">
						<DetailPanel title="Game details" icon={Gamepad2}>
							<DetailPanelSection>
								<KeyValueList>
									<KeyValueItem label="Identifier" className="font-mono">
										{game.entitySlug}
									</KeyValueItem>
									<KeyValueItem label="Game name">{gameData.name}</KeyValueItem>
									<KeyValueItem label="Release date">
										{gameData.releaseDate ? (
											new Date(gameData.releaseDate).toLocaleDateString()
										) : (
											<span className="text-meta">None</span>
										)}
									</KeyValueItem>
									<KeyValueItem label="Entity type">
										{game.entityType}
									</KeyValueItem>
									<KeyValueItem label="Game slug">
										<Badge variant={gameSlug as BadgeProps["variant"]}>
											{gameSlug}
										</Badge>
									</KeyValueItem>
									<KeyValueItem label="Last updated">
										{new Date(game._staging.lastModified).toLocaleDateString()}
									</KeyValueItem>
									{gameData.abbreviation && (
										<KeyValueItem label="Abbreviation">
											{gameData.abbreviation}
										</KeyValueItem>
									)}
									{gameData.website && (
										<KeyValueItem label="Official website">
											<a
												href={gameData.website}
												target="_blank"
												rel="noopener noreferrer"
												className="break-all text-primary-text hover:underline"
											>
												{gameData.website}
											</a>
										</KeyValueItem>
									)}
								</KeyValueList>
							</DetailPanelSection>
						</DetailPanel>
					</TabsContent>

					<TabsContent value="metadata">
						<StagingMetadataPanel staging={game._staging} />
					</TabsContent>
				</Tabs>
			</EntityDetailSplit>
		</div>
	)
}

function ThemedAsset({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs text-meta">{label}</span>
			{children}
		</div>
	)
}
